// Schemas for complex (object-typed) action settings that need a structured
// editor. Sourced from the Hyarchis HCS swagger spec; keep the field list
// minimal — anything not listed here is editable via the generic JSON fallback.

export interface ObjectFieldSchema {
  name: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'string-map' | 'any-map' | 'array' | 'object';
  description?: string;
  itemType?: string;
  objectTypeName?: string;
}

export interface ObjectSchema {
  typeName: string;
  description?: string;
  fields: ObjectFieldSchema[];
}

export const OBJECT_SCHEMAS: Record<string, ObjectSchema> = {
  AiSettings: {
    typeName: 'AiSettings',
    description:
      'AI model configuration: which model, temperature, prompt template and tools to use.',
    fields: [
      { name: 'model', type: 'string', description: 'LLM model name (e.g. gpt-4o-mini).' },
      {
        name: 'temperature',
        type: 'number',
        description: 'Sampling temperature (0-2). Lower = more deterministic.',
      },
      {
        name: 'outputLanguage',
        type: 'string',
        description: 'Target language code for the response (e.g. en, nl).',
      },
      {
        name: 'langfusePromptName',
        type: 'string',
        description: 'Name of the Langfuse-registered prompt template to use.',
      },
      {
        name: 'langfusePromptLabel',
        type: 'string',
        description: 'Langfuse prompt label/version (e.g. production, staging).',
      },
      {
        name: 'jsonResponse',
        type: 'boolean',
        description: 'Force the model to return strict JSON output.',
      },
      {
        name: 'jsonSchema',
        type: 'string',
        description: 'JSON schema the response should conform to.',
      },
      {
        name: 'promptVariables',
        type: 'string-map',
        description: 'Key/value pairs interpolated into the prompt template.',
      },
      {
        name: 'otherParams',
        type: 'any-map',
        description: 'Provider-specific extra parameters passed through to the model API.',
      },
      {
        name: 'mcpServers',
        type: 'array',
        itemType: 'McpServerReference',
        description: 'MCP servers exposing tools to the model.',
      },
      {
        name: 'mcpToolResultMaxSize',
        type: 'integer',
        description: 'Cap on the size of any single MCP tool result.',
      },
      {
        name: 'mcpToolCallLimit',
        type: 'integer',
        description: 'Maximum number of MCP tool calls per LLM run.',
      },
    ],
  },
  McpServerReference: {
    typeName: 'McpServerReference',
    fields: [
      { name: 'name', type: 'string', description: 'Friendly name for the MCP server.' },
      { name: 'url', type: 'string', description: 'Server endpoint URL.' },
      {
        name: 'authToken',
        type: 'string',
        description: 'Optional bearer token used to call the server.',
      },
    ],
  },
};

// Map a raw action-property type (as returned by GET /actions) to a known
// object schema, if any. Lookup is case-insensitive.
export const getObjectSchema = (typeName: string): ObjectSchema | null => {
  if (!typeName) return null;
  const lower = typeName.toLowerCase();
  for (const key of Object.keys(OBJECT_SCHEMAS)) {
    if (key.toLowerCase() === lower) return OBJECT_SCHEMAS[key];
  }

  return null;
};

// Best-effort "is this type structured?" check. Used to decide between the
// inline single-field editor and the nested structured editor.
export const isObjectType = (typeName: string | undefined | null): boolean => {
  if (!typeName) return false;
  if (getObjectSchema(typeName)) return true;
  const lower = typeName.toLowerCase();
  // Heuristic: anything ending in "Settings", "Config", "Options" is object-like.
  return /settings$|config$|configuration$|options$/.test(lower);
};
